import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import LocalStorageManager from '@Managers/Storage/LocalStorageManager';
import { ILocalStorageConfiguration, LocalStorageItemKeys } from '@Managers/Storage/LocalStorageManager.types';
import HttpService from '@Services/HttpService/HttpService';
import { deepMerge } from '@Tools/DeepMerge';
import paramToObject from '@Tools/ParamToObject';
import i18next from 'i18next';
import { CASINO_ID_WILDCARD, CONFIGURATION_FILES, EConfigurationNames, IConfigurations } from './ConfigFiles';
import { IVersionConfig } from './Configs';
import { IConnectionConfiguration, IHttpOptions, TUrlParameters } from './Configs/ConnectionConfig';
import { fontFamilyDefaults, IFontFamilyDefaults } from './Defaults';

type TSourcePromise = Promise<{ fileName: EConfigurationNames; index: number; fileData: any }>;
class ConfigLoader {
    static readonly ARRAYS_TO_MERGE = new Map([['win.particleEmitters', 'name']]);
    private readonly configByName: Map<EConfigurationNames, IConfigurations[EConfigurationNames]> = new Map();
    private readonly configurationsSources: Map<EConfigurationNames, string[]> = new Map();
    private authToken: string;
    public urlParameters!: TUrlParameters;
    public language: string;
    public baseUrl: string;
    public resourcePath: string;
    public gameResourcePath: string;
    public environmentsPath: string;
    public sharedResourcePath: string;
    public gameName: string;
    public casinoId: string;
    public fontFamily: IFontFamilyDefaults = fontFamilyDefaults;
    public authCreditionals: Array<string>;
    public connectionConfiguration: IHttpOptions;
    private localStorage: ILocalStorageConfiguration = {};

    constructor() {
        this.urlParameters = paramToObject();
        this.casinoId = (this.urlParameters.casinoId as string) ?? '';
        this.baseUrl = window.location.origin + window.location.pathname.replace(/\/index\.html$/, '/');
        this.environmentsPath = `${this.baseUrl}configuration/environments/`;
        this.resourcePath = this.baseUrl + 'configuration/games/';
        this.sharedResourcePath = this.resourcePath + '_shared';
        this.language = this.urlParameters.languageCode ?? 'en';
    }

    public getGameName(): string {
        return this.gameName;
    }

    public getAuthToken(): string {
        return this.authToken;
    }

    public setAuthToken(token: string): void {
        this.authToken = token;
    }

    public init(): void {
        Logger.log(LoggerLevel.Debug, { message: 'ConfigLoader INIT', source: LoggerSource.Ext });
    }

    private appServicesInitialization(): void {
        LocalStorageManager.init(this.urlParameters.gameId as string);

        const baseUrl = `${this.connectionConfiguration.baseUrl}${this.connectionConfiguration.apiVersion}`;

        HttpService.set({
            authenticateUrl: `${baseUrl}${this.connectionConfiguration.gateways.authenticate}`,
            userActionUrl: `${baseUrl}${this.connectionConfiguration.gateways.userAction}`,
            historyUrl: `${baseUrl}${this.connectionConfiguration.gateways.history}`,
            getBalanceUrl: `${baseUrl}${this.connectionConfiguration.gateways.getBalance}`,
            quitRequest: `${baseUrl}${this.connectionConfiguration.gateways.quit}`,
            getGameFixes: `${baseUrl}${this.connectionConfiguration.gateways.gameFixes}`
        });
    }

    public getConfiguration<K extends EConfigurationNames>(key: K): IConfigurations[K] {
        return this.configByName.get(key) as IConfigurations[K];
    }

    public getLocalStorageConfiguration(): ILocalStorageConfiguration {
        return this.localStorage;
    }

    private useDefaultCasinoId(): void {
        const versionConfig = this.getConfiguration(EConfigurationNames.VERSION);
        const defulatCasinoId = versionConfig.defaultCasinoId;

        if (!this.casinoId && defulatCasinoId) {
            this.casinoId = defulatCasinoId;
            this.urlParameters.casinoId = this.casinoId;
        }
    }

    public async loadConfigurationFiles(): Promise<void> {
        const [version, gameList] = await Promise.all([this.loadVersion(), this.loadGameList()]);
        this.configByName.set(version.fileName, version.fileData);
        this.configByName.set(gameList.fileName, gameList.fileData);
        this.gameName = this.urlParameters.gameId ? gameList.fileData[this.urlParameters.gameId] : '';
        this.gameResourcePath = this.resourcePath + this.gameName;
        this.useDefaultCasinoId();
        this.addConfigurationSources();
        const allLoads: TSourcePromise[] = this.loadAllSources(version.fileData);
        const results = await Promise.all(allLoads);

        for (const { fileName, fileData } of results.toSorted((a, b) => a.index - b.index)) {
            const currentConfig = this.configByName.get(fileName) ?? {};
            this.configByName.set(fileName, deepMerge(currentConfig, fileData, ConfigLoader.ARRAYS_TO_MERGE));
        }

        const integrationOverwrite = this.getConfiguration(EConfigurationNames.INTEGRATION);
        for (const config in integrationOverwrite) {
            const configKey = config as EConfigurationNames;
            const currentConfig = this.configByName.get(configKey) ?? {};
            this.configByName.set(configKey, deepMerge(currentConfig, integrationOverwrite[configKey]));
        }

        this.language = this.getConfiguration(EConfigurationNames.GAME).availableLanguages.includes(this.language) ? this.language : 'en';

        this.updateTranslations({
            pl: this.getConfiguration(EConfigurationNames.LANGUAGE_PL),
            en: this.getConfiguration(EConfigurationNames.LANGUAGE_EN),
            cs: this.getConfiguration(EConfigurationNames.LANGUAGE_CS),
            ro: this.getConfiguration(EConfigurationNames.LANGUAGE_RO),
            sk: this.getConfiguration(EConfigurationNames.LANGUAGE_SK),
            hr: this.getConfiguration(EConfigurationNames.LANGUAGE_HR)
        });

        this.updateConnection(this.getConfiguration(EConfigurationNames.CONNECTION));
        this.appServicesInitialization();
        this.readLocalStorage();
    }

    private readLocalStorage(): void {
        const keys = Object.keys(LocalStorageItemKeys);
        for (const keyItem in keys) {
            const key: LocalStorageItemKeys = LocalStorageItemKeys[keys[keyItem] as keyof typeof LocalStorageItemKeys];
            const localData = LocalStorageManager.readItem(key);
            if (localData != undefined) {
                this.localStorage[key] = localData;
            }
        }
    }

    private addConfigurationSources(): void {
        for (const file of Object.keys(CONFIGURATION_FILES) as EConfigurationNames[]) {
            if ([EConfigurationNames.VERSION, EConfigurationNames.GAME_LIST].includes(file)) continue;
            if (file.includes('language') && !file.includes(this.language.toUpperCase())) continue;

            if (file === EConfigurationNames.INTEGRATION) {
                this.configurationsSources.set(file, [`${this.environmentsPath}${CONFIGURATION_FILES[file].replace(CASINO_ID_WILDCARD, this.casinoId)}`]);
                continue;
            }

            const urls = [];

            urls.push(`${this.sharedResourcePath}/configuration/${CONFIGURATION_FILES[file]}`);

            if (![EConfigurationNames.CONNECTION, EConfigurationNames.UI_LAYER].includes(file))
                urls.push(`${this.gameResourcePath}/configuration/${CONFIGURATION_FILES[file]}`);

            this.configurationsSources.set(file, urls);
        }
    }

    private loadAllSources(version: IVersionConfig): TSourcePromise[] {
        const allLoads: TSourcePromise[] = [];

        for (const file of Object.keys(CONFIGURATION_FILES) as EConfigurationNames[]) {
            this.configurationsSources.get(file)?.forEach((url, index) => {
                allLoads.push(
                    new Promise((resolve) => {
                        this.loadJSONSource(url + `?v=${version.cacheVersion}`).then((res) => {
                            resolve({ fileName: file, index, fileData: res });
                        });
                    })
                );
            });
        }

        return allLoads;
    }

    private loadVersion(): TSourcePromise {
        return new Promise((resolve) => {
            const versionUrl = `${this.baseUrl}configuration/${CONFIGURATION_FILES[EConfigurationNames.VERSION]}?v=${Date.now().toString()}`;
            this.loadJSONSource(versionUrl).then((res) => {
                resolve({ fileName: EConfigurationNames.VERSION, index: 0, fileData: res });
            });
        });
    }

    private loadGameList(): TSourcePromise {
        return new Promise((resolve) => {
            const gameListUrl = `${this.baseUrl}configuration/${CONFIGURATION_FILES[EConfigurationNames.GAME_LIST]}?v=${Date.now().toString()}`;
            this.loadJSONSource(gameListUrl).then((res) => {
                resolve({ fileName: EConfigurationNames.GAME_LIST, index: 0, fileData: res });
            });
        });
    }

    private async loadJSONSource(url: string): Promise<any> {
        const response = await fetch(url);
        return response.ok ? await response.json() : {};
    }

    private updateTranslations(languageData: any): void {
        i18next.addResourceBundle(this.language, 'translation', languageData[this.language], true, true);
        i18next.changeLanguage(i18next.language);
    }

    private updateConnection(connectionData: IConnectionConfiguration): void {
        this.authCreditionals = connectionData.authCreditionals;
        this.connectionConfiguration = connectionData.connectionConfiguration;
    }
}

export default new ConfigLoader();
